export function createBrowserAlertDelivery({ key, startedAt = Date.now(), storage, NotificationAPI = globalThis.Notification, AudioAPI = globalThis.AudioContext || globalThis.webkitAudioContext, report = () => {}, focus = () => globalThis.window?.focus() }) {
  if (!storage) { try { storage = globalThis.localStorage } catch { /* Storage may be blocked independently of notifications. */ } }
  let active = true, audio = null, settings = { desktop: false, sound: false }
  const seen = new Set(), open = new Set()
  try { settings = { ...settings, ...JSON.parse(storage.getItem(key) || '{}') } } catch { /* Browser-local preference is optional. */ }
  function save(patch) {
    settings = { ...settings, ...patch }
    try { storage.setItem(key, JSON.stringify(settings)) } catch { report('Delivery settings could not be saved in this browser.') }
    return { ...settings }
  }
  async function enableDesktop() {
    if (!NotificationAPI) { report('Browser notifications are unavailable.'); return save({ desktop: false }) }
    try {
      const permission = await NotificationAPI.requestPermission()
      if (!active) return { ...settings }
      if (permission !== 'granted') report(permission === 'denied' ? 'Notifications blocked. Allow notifications in browser site settings.' : 'Notification permission was not granted.')
      return save({ desktop: permission === 'granted' })
    } catch { report('Browser notification permission could not be requested.'); return save({ desktop: false }) }
  }
  async function enableSound() {
    if (!AudioAPI) { report('Sound is unavailable in this browser.'); return save({ sound: false }) }
    try {
      audio ||= new AudioAPI()
      await audio.resume()
      if (!active) return { ...settings }
      if (audio.state !== 'running') throw Error('blocked')
      const next = save({ sound: true }); tone(); return next
    } catch { report('Sound is blocked. Click Enable sound to retry.'); return save({ sound: false }) }
  }
  function tone() {
    if (!audio || audio.state !== 'running') { report('Click Enable sound to activate audio for this session.'); return }
    try {
      const oscillator = audio.createOscillator(), gain = audio.createGain(), now = audio.currentTime
      oscillator.connect(gain); gain.connect(audio.destination)
      oscillator.frequency.setValueAtTime(880, now)
      gain.gain.setValueAtTime(0.12, now); gain.gain.exponentialRampToValueAtTime(0.001, now + 0.35)
      oscillator.onended = () => { oscillator.disconnect(); gain.disconnect() }
      oscillator.start(now); oscillator.stop(now + 0.35)
    } catch { report('Alert sound could not play. Enable sound to retry.') }
  }
  function deliver(receipt) {
    if (!active || !['price_alert','vwap_alert'].includes(receipt.type) || !receipt.id || seen.has(receipt.id)) return
    seen.add(receipt.id)
    if (seen.size > 1000) seen.delete(seen.values().next().value)
    const timestamp = Date.parse(receipt.firedAt || receipt.createdAt)
    if (!Number.isFinite(timestamp) || timestamp < Math.floor(startedAt / 1000) * 1000 || Date.now() - timestamp > 60000) return
    if (!settings.desktop && !settings.sound) return
    // A shared origin/owner receipt journal suppresses replay across reloads/tabs.
    const dispatch = () => {
      if (!active) return
      let delivered = []
      try { delivered = JSON.parse(storage.getItem(`${key}:delivered`) || '[]'); if (!Array.isArray(delivered)) delivered = [] } catch { /* In-memory dedup remains. */ }
      const desktopKey = `${receipt.id}:desktop`, soundKey = `${receipt.id}:sound`
      const desktop = settings.desktop && !delivered.includes(desktopKey)
      const sound = settings.sound && !delivered.includes(soundKey)
      const claimed = []
      if (desktop) {
        if (NotificationAPI?.permission === 'granted') {
          try {
            const notification = new NotificationAPI(receipt.title || 'Erevis alert', { body: receipt.message || '', tag: receipt.id, silent: true })
            open.add(notification); claimed.push(desktopKey)
            notification.onclick = () => { focus(); notification.close(); open.delete(notification) }
            notification.onclose = () => open.delete(notification)
          } catch { report('Browser notification failed. The alert remains in Erevis.') }
        } else report('Browser notifications are blocked. Check site permissions.')
      }
      if (sound) {
        if (audio?.state === 'running') { tone(); claimed.push(soundKey) }
        else report('Click Enable sound to activate audio for this session.')
      }
      // An inactive audio context must not consume another tab's audible delivery.
      if (claimed.length) try { storage.setItem(`${key}:delivered`, JSON.stringify([...delivered, ...claimed].slice(-1000))) } catch { /* Keep in-app delivery working. */ }

    }
    if (globalThis.navigator?.locks?.request) void navigator.locks.request(`${key}:delivery`, dispatch).catch(()=>report('Alert delivery is unavailable. The alert remains in Erevis.'))
    else dispatch()
  }
  return { get settings() { return { ...settings } }, get soundReady() { return audio?.state === 'running' }, save, enableDesktop, enableSound, deliver,
    close() { active = false; for (const notification of open) notification.close(); open.clear(); if (audio) void audio.close().catch(()=>{}); audio = null },
  }
}
